import { Seo } from "../components/Seo";
import React from "react";
import { User, EnvelopeSimple, Phone, MapPin } from "@phosphor-icons/react";
import { useContent } from "../context/ContentContext";
import { PageHero } from "../components/PageHero";
import { Reveal, Overline } from "../components/Reveal";
import { EnquiryForm } from "../components/EnquiryForm";
import { Pikorua } from "../components/Pikorua";

const Detail = ({ icon: Icon, label, value, href }) => (
  <div className="flex items-start gap-4 py-5 border-b border-white/10">
    <span className="grid place-items-center h-11 w-11 bg-gold/15 text-gold rounded-sm shrink-0">
      <Icon size={20} weight="duotone" />
    </span>
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-white/50">{label}</div>
      {href ? (
        <a href={href} className="text-white text-lg hover:text-gold transition-colors break-all">{value}</a>
      ) : (
        <div className="text-white text-lg">{value}</div>
      )}
    </div>
  </div>
);

export default function Contact() {
  const { content } = useContent();
  return (
    <div data-testid="contact-page">
      <Seo title="Contact — Te Manawa Table Tennis Association" description="Get in touch with Te Manawa Table Tennis Association. Based in Taupō, covering Taranaki, Bay of Plenty, Taupō and the central plateau." />
      <PageHero overline="Contact" title="Let's talk table tennis" subtitle="Whether you want to play, coach, volunteer or affiliate your club — we'd love to hear from you." />

      <section className="bg-cream py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-start">
          {/* Form */}
          <Reveal>
            <div className="bg-white border border-navy/10 p-8 md:p-10">
              <h2 className="font-heading text-3xl text-navy font-medium mb-6">Send us a message</h2>
              <EnquiryForm type="contact" submitLabel="Send message" />
            </div>
          </Reveal>

          {/* Details */}
          <Reveal delay={0.15}>
            <div className="relative bg-navy-dark overflow-hidden p-8 md:p-10">
              <div className="absolute inset-0 noise-overlay" />
              <Pikorua className="absolute -right-10 -bottom-10 w-64 h-64 text-gold opacity-[0.07]" stroke="#F2A900" />
              <div className="relative">
                <Overline className="text-gold">Key contact</Overline>
                <h2 className="font-heading text-3xl text-white font-medium mt-4 mb-2">Get in touch directly</h2>
                <Detail icon={User} label="Contact" value={content.contact_person} />
                <Detail icon={EnvelopeSimple} label="Email" value={content.contact_email} href={`mailto:${content.contact_email}`} />
                <Detail icon={Phone} label="Phone" value={content.contact_phone} href={`tel:${content.contact_phone}`} />
              </div>
            </div>
          </Reveal>
        </div>

        {/* Map placeholder */}
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-12">
          <Reveal>
            <div className="relative h-72 md:h-96 bg-navy overflow-hidden border border-navy/10" data-testid="map-placeholder">
              <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle, #F2A900 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
              <div className="absolute inset-0 grid place-items-center text-center px-6">
                <div>
                  <MapPin size={44} weight="fill" className="text-gold mx-auto" />
                  <p className="font-heading text-2xl md:text-3xl text-white font-medium mt-4">Based in Taupō</p>
                  <p className="text-white/80 mt-2">Waipahihi School Hall, 20 Parata Street, Two Mile Bay, Taupō 3330</p>
                  <p className="text-white/60 mt-2">Covering Taranaki, Bay of Plenty, Taupō & the central plateau</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
